import React, { Suspense } from 'react'

import { HydrationBoundary, dehydrate } from '@tanstack/react-query'
import Head from 'next/head'

import { QueryErrorBoundary } from '@/components/usaspending/DataErrorBoundary'

/**
 * Shared query options for ISR-dehydrated data.
 * Data is pre-fetched at build/revalidation time, so we never refetch client-side.
 */
export const ISR_QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const

/**
 * Standard page layout wrapper used by all opportunity detail pages.
 * Shared between Suspense fallback skeletons and rendered content.
 */
export function OpportunityPageLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div className="min-h-full w-full flex-1 text-primary-900 selection:bg-accent-100 selection:text-accent-900 dark:text-primary-100">
      <main className="mx-auto max-w-7xl px-4 pb-12 pt-6 md:px-6">
        {children}
      </main>
    </div>
  )
}

interface OpportunityDetailShellProps {
  seoTitle: string
  dehydratedState: ReturnType<typeof dehydrate>
  errorTitle: string
  notFoundTitle: string
  backHref: string
  backLabel: string
  fallback: React.ReactNode
  children: React.ReactNode
}

/**
 * Shared page shell for ISR opportunity detail pages.
 *
 * Composes Head, QueryErrorBoundary, HydrationBoundary, and Suspense
 * so each page only declares what's unique to it.
 */
export function OpportunityDetailShell({
  seoTitle,
  dehydratedState,
  errorTitle,
  notFoundTitle,
  backHref,
  backLabel,
  fallback,
  children,
}: OpportunityDetailShellProps) {
  return (
    <>
      <Head>
        <title>{seoTitle} - Sweetspot</title>
      </Head>
      <QueryErrorBoundary
        title={errorTitle}
        notFoundTitle={notFoundTitle}
        backHref={backHref}
        backLabel={backLabel}
        fullPage
      >
        <HydrationBoundary state={dehydratedState}>
          <Suspense fallback={fallback}>{children}</Suspense>
        </HydrationBoundary>
      </QueryErrorBoundary>
    </>
  )
}
